/* eslint-disable react-hooks/exhaustive-deps */

import {
    ComponentProps,
    FC,
    MouseEvent,
    useContext,
    useEffect,
    useState
} from 'react';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { ApolloError } from '@apollo/client';
import {
    Breadcrumbs,
    PaginationOnPageChangeEvent,
    useModal
} from '@blueprint/ui';
import triggerTracking from '@blueprint/tracking';
import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk';
import {
    getOptimizelyFeatureFlag,
    useSelectForcedFeatures
} from '@zoopla/optimizely';

import {
    filtersValuesSelector,
    identifierSelector
} from '../../reducers/filters';
import {
    resetResultsAsync,
    selectPageNumber,
    totalPagesSelector
} from '../../reducers/searchResults';
import { selectCurrentSortOrder } from '../../reducers/listingsSortOrder';
import { Listings } from '../Listings';
import { SearchResultsHeader } from './SearchResultsHeader';
import { DiscoverMore } from '../banners/DiscoverMore';
import {
    PaginationContainer,
    SearchResultsLayoutGroup,
    StyledPagination
} from '../Layout/Layout.styles';
import {
    Links,
    ProductTrackingObject,
    SearchContext,
    SearchType,
    SeoBlurb,
    ServerData
} from '../SearchContext';
import { Listing } from '../Listings/Listings.types';
import {
    buildCanonicalPath,
    buildQueryParamsString,
    segmentSearchEvent
} from '../helpers';
import { activateMobileBanner } from '../helpers/activateMobileBanner';
import { getProductImpression } from '../FeaturedPropertyList/featuredPropertyTracking';
import { PropertyOrPromoItem } from '../helpers/featuredProperties';
import FeaturedPropertyList from '../FeaturedPropertyList';
import { identifyUser, trackPage } from '@zoopla/tracking';
import { getJSONLD } from '../Seo/schemaMarkup';
import { shouldIndexPath } from '../Seo/noFollow';
import { BreadcrumbsShowMobileWrapper } from './SearchResults.styles';
import { SortOrderDropdown } from './SearchResultsHeader/SortOrderDropdown';
import { updatePathPageNumber } from 'search/helpers/updatePathPageNumber';
import { useSaveListing } from 'search-map-view/components/AuthActions/hooks/useSaveListing';
import { useBreakpointListener } from '@zoopla/dom-util';
import { breakpoints } from '../../search/common/constants';
import { useTheme } from '@emotion/react';
import { removeEverythingAfterHash } from 'shared/helpers';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';
import AuthModal from 'shared/components/AuthModal/AuthModal';
import { Loading } from 'shared/components/Loading';
import Head from 'shared/components/Head/Head';
import { EventNames } from '@zoopla/tracking';

const EmailAllAgentsWrapper = dynamic<
    ComponentProps<
        typeof import('../EmailAgents/EmailAllAgentsWrapper').EmailAllAgentsWrapper
    >
>(
    () =>
        import('../EmailAgents/EmailAllAgentsWrapper').then(
            (module) => module.EmailAllAgentsWrapper
        ),
    {
        ssr: false
    }
);

export type SearchResultProps = {
    regularListings: Listing[];
    extendedListings: Listing[];
    featuredProperties: PropertyOrPromoItem[];
    canonicalPath: string;
    searchResults: ServerData['searchResults'];
    discoverMore: ServerData['discoverMore'];
    breadcrumbs: ServerData['breadcrumbs'];
    loading: boolean;
    transactionType: SeoBlurb['transactionType'];
    error?: ApolloError;
    links: Links;
};

const SearchResults: FC<SearchResultProps & WithOptimizelyProps> = ({
    regularListings,
    extendedListings,
    featuredProperties,
    canonicalPath: canonicalPathFromServer,
    searchResults,
    breadcrumbs,
    discoverMore,
    loading,
    transactionType,
    links,
    error,
    optimizely
}) => {
    const reduxDispatch = useDispatch();
    const router = useRouter();
    const { query, asPath, route } = router;
    const path = removeEverythingAfterHash(asPath);

    const { state, dispatch } = useContext(SearchContext);

    const {
        searchType,
        currentSearchSource,
        userAlertId,
        isEditSavedSearchBannerVisible,
        previousSavedSearchUri,
        savedSearchAndAlerts,
        favourites,
        actionAfterAuth,
        seoAccordions
    } = state;

    const isLoggedIn = useIsLoggedIn();

    const filters = useSelector(filtersValuesSelector);
    const currentSortOrder = useSelector(selectCurrentSortOrder);
    const { selectedValue: identifierSelectedValue } =
        useSelector(identifierSelector);

    const totalPages = useSelector(totalPagesSelector);
    const pageNumber = useSelector(selectPageNumber);

    const [canonicalPath, setCanonicalPath] = useState(canonicalPathFromServer);
    const isBelowShowSidebarBreakpoint = useBreakpointListener(
        breakpoints.lsrpSidebar,
        'LT'
    );
    const theme = useTheme();
    const isMobile = useBreakpointListener(theme.breakpoints[1], 'LT');

    const [internalPromoParam, setInternalPromoParam] = useState('');

    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useModal();
    const [authHeader, setAuthHeader] = useState<string | undefined>(undefined);

    const featureFlagOverrides = useSelectForcedFeatures();
    const isMobileBannerEnabled = getOptimizelyFeatureFlag(
        'mobile_app_banner',
        featureFlagOverrides,
        optimizely
    );

    const isPhoneClickToRevealEnabled = getOptimizelyFeatureFlag(
        'lex_911_click_to_reveal',
        featureFlagOverrides,
        optimizely
    );

    const isShowHaloFeatureEnabled = getOptimizelyFeatureFlag(
        'show_listing_halo',
        featureFlagOverrides,
        optimizely
    );

    const authenticateSaveToggle = useSaveListing(
        dispatch,
        favourites.listings,
        isLoggedIn,
        searchResults?.analyticsTaxonomy,
        setAuthHeader,
        openAuthModal,
        'search results'
    );

    useEffect(() => {
        const isLSRP =
            searchType === SearchType.ForSale ||
            searchType === SearchType.ToRent;
        if (isLSRP) {
            activateMobileBanner(isMobileBannerEnabled, isMobile);
        }
    }, [isMobile]);

    useEffect(() => {
        if (isMobile) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'optimizely_feature',
                eventAction: 'show_listing_halo',
                eventLabel: isShowHaloFeatureEnabled ? 'alternative' : 'control'
            });
        }
    }, [isMobile]);

    useEffect(() => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'optimizely',
            eventAction: 'lex_911_click_to_reveal',
            eventLabel: isPhoneClickToRevealEnabled ? 'variant' : 'original',
            eventNonInteraction: true
        });
    }, [isPhoneClickToRevealEnabled]);

    const resetActionAfterAuth = async () => {
        dispatch({
            type: 'SET_ACTION_AFTER_AUTH',
            payload: {
                action: () => null,
                eventLabel: 'save_listing'
            }
        });
    };

    useEffect(() => {
        const promoParam = buildQueryParamsString(query, 1);

        setInternalPromoParam(promoParam);
    }, [query.q]);

    // All tracking handled in one effect
    useEffect(() => {
        if (!searchResults?.analyticsTaxonomy?.page) {
            return;
        }
        const { analyticsTaxonomy: analyticsTaxonomyFromResponse } =
            searchResults;

        const analyticsTaxonomy = {
            ...analyticsTaxonomyFromResponse,
            location: analyticsTaxonomyFromResponse.areaName
        };

        let featuredImpressions: ProductTrackingObject[] = [];

        if (!isBelowShowSidebarBreakpoint && featuredProperties.length != 0)
            featuredImpressions = featuredProperties.map((item, index) =>
                getProductImpression(item, index + 1)
            );

        const analyticsEcommerce = {
            currencyCode: searchResults.analyticsEcommerce.currencyCode,
            impressions: [
                ...(searchResults.analyticsEcommerce.impressions ?? []),
                ...featuredImpressions
            ]
        };

        trackPage(
            analyticsTaxonomy.page,
            analyticsTaxonomy,
            analyticsEcommerce
        );
        segmentSearchEvent({
            eventName: EventNames.SEARCH,
            analyticsTaxonomy,
            currentSearchSource,
            searchCategory: searchResults.seoBlurb.category,
            filters
        });
    }, [searchResults]);

    useEffect(() => {
        identifyUser(window);
    }, []);

    useEffect(() => {
        const uri = searchResults?.metaInfo?.canonicalUri || '';
        const newCanonicalPath = buildCanonicalPath(uri);

        setCanonicalPath(newCanonicalPath);
    }, [searchResults?.metaInfo?.canonicalUri]);

    /**
       NOTE:
       This param is for internal ads in featured property list (Map and New Home link)
       Both links start a new search using search param in results page, e.g. "q=london&radius=0"
    */
    const currentPage = typeof query.pn === 'string' ? parseInt(query.pn) : 1;

    const handleChangePage = async (
        onPageChangeEvent: PaginationOnPageChangeEvent
    ) => {
        onPageChangeEvent.nativeEvent.preventDefault();

        if (onPageChangeEvent.targetPage === currentPage) return;

        if (onPageChangeEvent.source === 'next') {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `pagination_next`
            });
        } else if (onPageChangeEvent.source === 'previous') {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `pagination_back`
            });
        } else {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `page_${onPageChangeEvent.targetPage}`
            });
        }

        const targetPage = pageNumberToHref(onPageChangeEvent.targetPage);
        targetPage && router.push(targetPage);
    };

    const pageNumberToHref = (pageNumber: number): string | undefined => {
        const currentPath = router.asPath.replace('&view_type=list', '');

        if (currentPage === pageNumber) return undefined;

        return updatePathPageNumber(currentPath, pageNumber);
    };

    const onResetFilters = async (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();

        const SEARCH_SOURCE = 'facets';
        reduxDispatch(
            resetResultsAsync(
                query,
                router,
                searchType,
                SEARCH_SOURCE,
                currentSortOrder,
                userAlertId
            )
        );
    };

    const sortOrderDropdown = (
        <SortOrderDropdown
            excludeRadiusFromResults={extendedListings.length > 0}
            userAlertId={userAlertId}
            searchType={searchType}
            dispatch={dispatch}
        />
    );

    const authModal = (
        <AuthModal
            isShowing={isAuthModalShowing}
            close={closeAuthModal}
            actionAfterAuth={actionAfterAuth}
            resetActionAfterAuth={resetActionAfterAuth}
            heading={authHeader}
        />
    );

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            <Head
                pageTitle={searchResults?.metaInfo?.title || ''}
                pageDescription={searchResults?.metaInfo?.description || ''}
                canonicalPath={canonicalPath}
                renderSocialSharingTags={true}
                index={() =>
                    shouldIndexPath(
                        asPath,
                        query,
                        searchResults?.seoBlurb?.category || '',
                        pageNumber,
                        regularListings.length,
                        extendedListings.length
                    )
                }
                schema={() =>
                    getJSONLD(
                        asPath,
                        searchResults?.metaInfo?.canonicalUri || '',
                        breadcrumbs || []
                    )
                }
            />
            <SearchResultsHeader
                breadcrumbs={breadcrumbs || []}
                previousSavedSearchUri={previousSavedSearchUri}
                savedSearchAndAlertsUri={savedSearchAndAlerts?.uri}
                userAlertId={userAlertId}
                isEditSavedSearchBannerVisible={isEditSavedSearchBannerVisible}
                pageTitle={searchResults?.title || ''}
                mapIconLink={links?.mapView || ''}
                isLoggedIn={isLoggedIn}
                sortOrderDropdown={sortOrderDropdown}
            />
            <SearchResultsLayoutGroup>
                <FeaturedPropertyList
                    properties={featuredProperties}
                    sectionType={transactionType}
                    propertyType={seoAccordions[0]?.propertyType}
                    identifier={identifierSelectedValue as string}
                    searchParam={internalPromoParam}
                />

                <Listings
                    searchType={searchType}
                    analyticsTaxonomyIdentifier={
                        searchResults?.analyticsTaxonomy?.searchIdentifier || ''
                    }
                    analyticsTaxonomy={searchResults?.analyticsTaxonomy}
                    analyticsEcommerce={searchResults?.analyticsEcommerce}
                    searchCategory={searchResults?.seoBlurb?.category ?? ''}
                    filters={filters}
                    currentSearchSource={currentSearchSource}
                    error={error}
                    regularListings={regularListings}
                    extendedListings={extendedListings}
                    onResetFilters={onResetFilters}
                    favouritesListings={favourites.listings}
                    authenticateSaveToggle={authenticateSaveToggle}
                    authModal={authModal}
                    pageNumber={pageNumber}
                    showHalo={isShowHaloFeatureEnabled}
                    route={route}
                    isPhoneClickToRevealEnabled={isPhoneClickToRevealEnabled}
                />
                {regularListings.length > 0 && (
                    <PaginationContainer data-testid="pagination">
                        <StyledPagination
                            previousLabel="< Back"
                            nextLabel="Next >"
                            totalPages={totalPages}
                            currentPage={currentPage}
                            pageNumberToHref={pageNumberToHref}
                            onPageChange={handleChangePage}
                        />
                    </PaginationContainer>
                )}

                <EmailAllAgentsWrapper
                    path={path}
                    searchType={searchType}
                    location={filters?.location?.value}
                    isUserLoggedIn={isLoggedIn}
                    createAlert={links?.createAlert || ''}
                />

                <BreadcrumbsShowMobileWrapper>
                    <Breadcrumbs
                        data-testid="mobile-breadcrumbs"
                        data={breadcrumbs || []}
                    />
                </BreadcrumbsShowMobileWrapper>

                <DiscoverMore
                    findAgentsUri={discoverMore?.findAgentsUri || ''}
                    housePricesUri={discoverMore?.housePricesUri || ''}
                    searchArea={
                        searchResults?.analyticsTaxonomy?.areaName || ''
                    }
                />
            </SearchResultsLayoutGroup>
        </>
    );
};

export default withOptimizely(SearchResults);
